/**
 * Grid Dashboard — shared types for the widget grid system.
 *
 * Used by both the Trading Dashboard (LayoutContext) and the Market Monitor
 * dashboard provider. Each page owns its own WidgetRegistry instance and
 * passes it into GridCanvas / WidgetPicker / WidgetShell as a prop.
 */

import { ComponentType } from 'react';

export interface WidgetSize {
  width: number;
  height: number;
}

export interface WidgetInstanceProps {
  instanceId: string;
  config?: Record<string, unknown>;
}

export interface WidgetContract {
  widgetType: string;
  displayName: string;
  description: string;
  icon?: string;
  component: ComponentType<WidgetInstanceProps>;
  defaultSize: WidgetSize;
  minSize: WidgetSize;
  maxSize?: WidgetSize;
  /**
   * Hide the shared shell title bar. Widgets that opt in must render their
   * own drag handle with the WIDGET_DRAG_HANDLE_CLASS to keep grid dragging
   * available.
   */
  hideShellTitleBar?: boolean;
  /**
   * Hide the shell's floating × remove button. Widgets that opt in must
   * provide their own remove affordance and route it through the
   * controller / `onRemove` prop.
   *
   * Combined with `hideShellTitleBar: true`, the WidgetShell renders no
   * chrome at all — useful for widgets whose body already provides a
   * complete card (title, remove, etc.) and would otherwise show a
   * redundant outer frame.
   */
  hideShellRemoveButton?: boolean;
}

export interface WidgetPlacement {
  instanceId: string;
  widgetType: string;
  x: number;
  y: number;
  w: number;
  h: number;
  version?: number;
  /** Pinned widgets are frozen on the canvas: not draggable, not resizable, and never pushed by other widgets. */
  pinned?: boolean;
  config?: Record<string, unknown>;
}

/**
 * Page-scoped widget registry. Each page (Trading, Market Monitor, …)
 * constructs its own instance via createWidgetRegistry() so widget sets
 * never leak across pages.
 */
export interface WidgetRegistry {
  register(contract: WidgetContract): void;
  get(widgetType: string): WidgetContract | undefined;
  getAll(): WidgetContract[];
}

export const WIDGET_DRAG_HANDLE_CLASS = 'widget-drag-handle';
